import { z } from 'zod';
import { uuidSchema } from './common.schema';

// ---------------------------------------------------------------------------
// Shared lookup shape — all 4 lookup tables (brands, categories, forms,
// manufacturers) have the same column pattern: id + name + timestamps.
// Note: brands/categories/forms do NOT have updated_at in the DB.
// ---------------------------------------------------------------------------
const lookupBaseSchema = z.object({
  name: z.string().max(300),
  created_at: z.string(),
});

const lookupResponseDates = {
  created_at: z.coerce.date().transform((d) => d.toISOString()),
};

// ---------------------------------------------------------------------------
// Manufacturer  (has updated_at unlike the other 3 tables)
// ---------------------------------------------------------------------------
export const ManufacturerSchema = lookupBaseSchema.extend({
  manufacturer_id: uuidSchema,
  updated_at: z.string(),
});
export type Manufacturer = z.infer<typeof ManufacturerSchema>;

export const ManufacturerResponseSchema = ManufacturerSchema.extend({
  ...lookupResponseDates,
  updated_at: z.coerce.date().transform((d) => d.toISOString()),
});

export const CreateManufacturerRequestSchema = z.object({ name: z.string().min(1).max(300) });
export type CreateManufacturerRequest = z.infer<typeof CreateManufacturerRequestSchema>;

// ---------------------------------------------------------------------------
// Brand
// ---------------------------------------------------------------------------
export const BrandSchema = lookupBaseSchema.extend({
  brand_id: uuidSchema,
});
export type Brand = z.infer<typeof BrandSchema>;

export const BrandResponseSchema = BrandSchema.extend(lookupResponseDates);

export const CreateBrandRequestSchema = z.object({ name: z.string().min(1).max(200) });
export type CreateBrandRequest = z.infer<typeof CreateBrandRequestSchema>;

// ---------------------------------------------------------------------------
// Category
// ---------------------------------------------------------------------------
export const CategorySchema = lookupBaseSchema.extend({
  category_id: uuidSchema,
});
export type Category = z.infer<typeof CategorySchema>;

export const CategoryResponseSchema = CategorySchema.extend(lookupResponseDates);

export const CreateCategoryRequestSchema = z.object({ name: z.string().min(1).max(200) });
export type CreateCategoryRequest = z.infer<typeof CreateCategoryRequestSchema>;

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------
export const FormSchema = lookupBaseSchema.extend({
  form_id: uuidSchema,
});
export type Form = z.infer<typeof FormSchema>;

export const FormResponseSchema = FormSchema.extend(lookupResponseDates);

export const CreateFormRequestSchema = z.object({ name: z.string().min(1).max(100) });
export type CreateFormRequest = z.infer<typeof CreateFormRequestSchema>;

// ---------------------------------------------------------------------------
// Shared lookup type / discriminated union used by the generic API
// ---------------------------------------------------------------------------
export const LOOKUP_TYPES = ['manufacturer', 'brand', 'category', 'form'] as const;
export type LookupType = (typeof LOOKUP_TYPES)[number];

/** Lookup types that support POST (create). Only manufacturer and brand. */
export const POST_LOOKUP_TYPES = ['manufacturer', 'brand'] as const;
export type PostLookupType = (typeof POST_LOOKUP_TYPES)[number];

/** Generic item shape returned by GET /api/v1/lookups?type=... */
export const LookupItemSchema = z.object({
  name: z.string(),
  /** Populated when type=brand */
  manufacturer_name: z.string().optional(),
  /** Populated when type=form */
  measurement_metric: z.string().optional(),
});
export type LookupItem = z.infer<typeof LookupItemSchema>;
